import { ref, reactive, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { defineStore } from 'pinia'
import { goTo, updatePageTitle } from '@/js/booking/booking-search-router'
import Storage from '@/js/global/localstorage'
import type { Institution } from '@/js/booking/institution'
import type { Service } from '@/js/booking/service'
import type { Specialist } from '@/js/booking/specialist'


type ItemType = 'institution'|'service'|'specialist'

type InstitutionItem = {
    type: 'institution'
    item: Institution,
}
type ServiceItem = {
    type: 'service'
    item: Service,
}
type SpecialistItem = {
    type: 'specialist'
    item: Specialist,
}


export const useBookingStore = defineStore('BookingStore', () => {
    const route = useRoute()


    const selectedInstitution = ref<Institution|null>(null)
    const selectedService     = ref<Service|null>(null)
    const selectedSpecialist  = ref<Specialist|null>(null)


    // Ārstu specialitātes, ko rādīt dropdownā.
    const specialities = ref<{code: string}[]>([])

    // Iestāžu karte.
    const institutionMap = reactive({
        contentLoading: true,
    })

    // Navigācija notiek no videokonsultāciju sadaļas
    const isFromTelemedicine = ref(false)

    const filter = reactive({
        PaidTime:           Storage.getBoolean('FilterTimeSlot:PaidTime'),
        GovernmentPaidTime: Storage.getBoolean('FilterTimeSlot:GovernmentPaidTime'),
        InPerson:           Storage.getBoolean('FilterTimeSlot:InPerson'),
        Remote:             Storage.getBoolean('FilterTimeSlot:Remote'),

        searchText: {
            institutions: '',
            services: '',
            specialists: '',
            specialistsWhenInstitutionSelected: '',
            servicesWhenInstitutionSelected: '',
            institutionsWhenServiceSelected: '',
        },

        selectedSpecialities: [] as string[],
        selectedSpecialitiesWhenInstitutionSelected: [] as string[],
    })

    watch(() => filter.PaidTime,           newValue => Storage.setItem('FilterTimeSlot:PaidTime', newValue))
    watch(() => filter.GovernmentPaidTime, newValue => Storage.setItem('FilterTimeSlot:GovernmentPaidTime', newValue))
    watch(() => filter.InPerson,           newValue => Storage.setItem('FilterTimeSlot:InPerson', newValue))
    watch(() => filter.Remote,             newValue => Storage.setItem('FilterTimeSlot:Remote', newValue))


    watch(specialities, () => {
        const specialitiesFilteredOut = (selectedSpecialities: string[]) => selectedSpecialities.some(specialityCode => {
            return !specialities.value.some(speciality => speciality.code.includes(specialityCode))
        })

        if (specialitiesFilteredOut(filter.selectedSpecialities)) {
            filter.selectedSpecialities = []
        }

        if (specialitiesFilteredOut(filter.selectedSpecialitiesWhenInstitutionSelected)) {
            filter.selectedSpecialitiesWhenInstitutionSelected = []
        }
    })
    
    watch([selectedInstitution, selectedService, selectedSpecialist], updatePageTitle)

    /**
     * Pārslēdzoties starp iestādēm meklēšanas skatā, nodzēš filtrus, kas izmantoti iepriekšējā
     */
    watch(() => selectedInstitution.value?.institutionCode, (oldInstitutionCode, newInstitutionCode) => {
        if (oldInstitutionCode != newInstitutionCode) {
            filter.searchText.specialistsWhenInstitutionSelected = ''
            filter.searchText.servicesWhenInstitutionSelected = ''
            filter.selectedSpecialitiesWhenInstitutionSelected = []
        }
    })

    /**
     * Pārslēdzoties starp pakalpojumiem meklēšanas skatā, nodzēš filtrus, kas izmantoti iepriekš
     */
    watch(() => selectedService.value?.serviceCode, (oldServiceCode, newServiceCode) => {
        if (oldServiceCode != newServiceCode) {
            filter.searchText.institutionsWhenServiceSelected = ''
        }
    })

    const isFilterTopical = (new Date().getTime() - (Storage.getTimestamp('BookingSearch:LastVisit') ?? 0)) < 20 * 60 * 1000

    // Kešatmiņā saglabātie filtri ir aktuāli tikai 20 minūtes pēc pēdējās navigācijas
    if (! isFilterTopical) {
        resetFilter()
    }

    /**
     * Atiestata meklēšanas filtrus uz to noklusējuma vērtībām
     */
    function resetFilter() {
        filter.PaidTime = false
        filter.GovernmentPaidTime = false
        filter.InPerson = false
        filter.Remote = false
        filter.selectedSpecialities = []
        filter.selectedSpecialitiesWhenInstitutionSelected = []
        filter.searchText = {
            institutions: '', institutionsWhenServiceSelected: '',
            services: '', servicesWhenInstitutionSelected: '',
            specialists: '', specialistsWhenInstitutionSelected: '',
        }
    }

    /**
     * Atgriež iestādi, pakalpojumu un ārstu tādā secībā kādā tie tika izvēlēti.
     */
    const selectedItems = computed(() => {
        const selectedTypes: string[] = []
        const result: (InstitutionItem|ServiceItem|SpecialistItem)[] = []

        if (route.query.Hierarchy) {
            for (const type of (route.query.Hierarchy as string).split('.')) {
                if (selectedTypes.includes(type)) {
                    continue
                }

                selectedTypes.push(type)

                if (type == 'institution') {
                    if (selectedInstitution.value) {
                        result.push({ type, item: selectedInstitution.value })
                    }

                } else if (type == 'service') {
                    if (selectedService.value) {
                        result.push({ type, item: selectedService.value })
                    }

                } else if (type == 'specialist') {
                    if (selectedSpecialist.value) {
                        result.push({ type, item: selectedSpecialist.value })
                    }
                }
            }
        }

        if (route.query.InstitutionCode && selectedInstitution.value && !selectedTypes.includes('institution')) {
            result.push({
                type: 'institution',
                item: selectedInstitution.value
            })
        }

        if (route.query.ServiceCode && selectedService.value && !selectedTypes.includes('service')) {
            result.push({
                type: 'service',
                item: selectedService.value
            })
        }

        if (route.query.SpecialistCode && selectedSpecialist.value && !selectedTypes.includes('specialist')) {
            result.push({
                type: 'specialist',
                item: selectedSpecialist.value
            })
        }

        return result
    })


    function selectInstitution(institution: Institution) {
        selectedInstitution.value = institution

        if (selectedService.value) {
            goTo('AvailableTimeSlots', {
                query: {
                    InstitutionCode: institution.institutionCode,
                    ServiceCode: selectedService.value.serviceCode,
                    Hierarchy: 'service.institution'
                }
            })

        } else if (selectedSpecialist.value) {
            goTo('AvailableTimeSlots', {
                query: {
                    InstitutionCode: institution.institutionCode,
                    SpecialistCode: selectedSpecialist.value.specialistCode,
                    SpecialityCode: selectedSpecialist.value.specialityCode,
                    Hierarchy: 'specialist.institution'
                }
            })
            
        } else {
            goTo('InstitutionPage', {
                params: {
                    institutionCode: institution.institutionCode,
                }
            })
        }
    }


    function selectService(service: Service) {
        selectedService.value = service

        if (selectedInstitution.value) {
            goTo('AvailableTimeSlots', {
                query: {
                    InstitutionCode: selectedInstitution.value.institutionCode,
                    ServiceCode: selectedService.value.serviceCode,
                    Hierarchy: 'institution.service'
                }
            })

        } else {
            if (route.name == 'InstitutionMap') {
                goTo('InstitutionMap', {
                    params: {
                        section: 'Institutions'
                    },
                    query: {
                        ServiceCode: service.serviceCode,
                    }
                })

            } else {
                goTo('Institutions', {
                    query: {
                        ServiceCode: service.serviceCode,
                    }
                })
            }
        }
    }


    function selectSpecialist(specialist: Specialist) {
        selectedSpecialist.value = specialist

        goTo('AvailableTimeSlots', {
            query: {
                InstitutionCode: selectedInstitution.value?.institutionCode,
                ServiceCode: selectedService.value?.serviceCode,
                SpecialistCode: specialist.specialistCode,
                SpecialityCode: specialist.specialityCode,
                Hierarchy: route.query.Hierarchy
            }
        })
    }


    function selectItem(type: ItemType, item: Institution|Service|Specialist) {
        if (type == 'institution') {
            selectInstitution(item as Institution)

        } else if (type == 'service') {
            selectService(item as Service)

        } else if (type == 'specialist') {
            selectSpecialist(item as Specialist)
        }
    }


    /**
     * Navigācija notiek no telemedicīnas sadaļas un lietotājs nav mainījis
     * pakalpojuma tipa filtrus
     */
    function isTelemedicineSelected() {
        if (isFromTelemedicine.value && !filter.InPerson && filter.Remote) {
            return true
        } else {
            isFromTelemedicine.value = false
            return false
        }
    }

    /**
     * Saglabā statusu par navigāciju no telemedicīnas sadaļas un uzstāda filtrus
     */
    function browseTelemedicine() {
        isFromTelemedicine.value = true
        filter.InPerson = false
        filter.Remote = true
    }


    function isFirstSelectedItem(type: ItemType) {
        return selectedItems.value[0]?.type === type
    }


    return {
        filter,
        route,

        selectedInstitution,
        selectedService,
        selectedSpecialist,

        specialities,
        institutionMap,
        selectedItems,

        selectInstitution,
        selectService,
        selectSpecialist,
        selectItem,
        resetFilter,

        browseTelemedicine,
        isTelemedicineSelected,

        isFirstSelectedItem,
    }
})
